import { useState } from "react";
import { toast } from "@components/toast/Toast";
import { getServerErrorMessage } from "@api";
import EventDetails from "@areas/calendar/displays/EventDetails";
import EventForm from "@areas/calendar/flows/EventForm";
import {
  useCalendars,
  useCancelProjectedAction,
  useDeleteCalendarItem,
  useSaveCalendarItem,
} from "@areas/calendar/hooks/useCalendar";

// Opens the calendar's own item modal (EventDetails, and EventForm for edits) for a scheduled
// item, wired to the same calendar mutations the calendar page uses — so clicking an item here
// behaves identically to clicking it in the calendar.
const ScheduleItemModal = ({ item, onClose }) => {
  const { data: calendars } = useCalendars();
  const calendar = calendars?.[0];
  const [formState, setFormState] = useState(null); // { item } while editing

  const saveItem = useSaveCalendarItem(calendar?.href);
  const deleteItem = useDeleteCalendarItem(calendar?.href);
  const cancelAction = useCancelProjectedAction(calendar?.href);

  const handleDelete = () => {
    deleteItem.mutate(
      { item },
      {
        onSuccess: () => {
          onClose();
          toast({ description: "Calendar item deleted" });
        },
        onError: () => toast({ description: "Couldn't delete the calendar item" }),
      },
    );
  };

  const handleCancelAction = (actionId) => {
    cancelAction.mutate(
      { actionId, item },
      {
        onSuccess: () => {
          onClose();
          toast({ description: "Scheduled work cancelled" });
        },
        onError: (error) =>
          toast({
            description: getServerErrorMessage(error) || "Couldn't cancel the scheduled work",
          }),
      },
    );
  };

  const handleSave = (updated) => {
    saveItem.mutate(
      { item: updated },
      {
        onSuccess: () => {
          setFormState(null);
          onClose();
          toast({ description: "Calendar item updated" });
        },
        onError: (error) =>
          toast({
            description:
              error?.response?.status === 412
                ? "This item changed elsewhere — refresh and try again"
                : "Couldn't save the calendar item",
          }),
      },
    );
  };

  return (
    <>
      <EventDetails
        item={item}
        isOpen={!!item && !formState}
        onClose={onClose}
        onEdit={() => setFormState({ item })}
        onDelete={handleDelete}
        deleting={deleteItem.isPending}
        onCancelAction={handleCancelAction}
        cancelling={cancelAction.isPending}
      />

      <EventForm
        isOpen={!!formState}
        onClose={() => setFormState(null)}
        onSave={handleSave}
        saving={saveItem.isPending}
        item={formState?.item ?? null}
        defaultDate={null}
      />
    </>
  );
};

export default ScheduleItemModal;
